/**
 * InviteFlow — V2 invite/registration entry point.
 *
 * Triggered by `?invite=TOKEN` on /driver-vite/dist/. Resolves the token via
 * POST /api/auth/invite-resolve and branches on the returned `kind`:
 *
 *   invalid             — token missing/forged/expired. Terminal screen.
 *   already_claimed     — account already has a password. Terminal screen + log-in CTA.
 *   new                 — token valid, no employees row. Wizard collects everything;
 *                         submit creates registration_requests pending review.
 *   existing_incomplete — employee row exists without password. Wizard prefilled;
 *                         submit updates employee directly (per product call).
 *
 * Submit posts `/api/auth/complete-profile` with `invite_token` included so the
 * backend re-verifies the token and rejects cross-account driver_id substitution.
 */
import React, { useEffect, useState } from 'react';
import { C, FONT, GLASS, GLASS_STRONG, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { apiPost } from './driver-session.js';

type Kind = 'loading' | 'invalid' | 'already_claimed' | 'new' | 'existing_incomplete';

interface PrefilledEmployee {
  driver_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  dob?: string;
  dl_number?: string;
  shirt_size?: string;
  pants_size?: string;
  jacket_size?: string;
}

interface InviteResolveResponse {
  kind?: 'invalid' | 'new' | 'existing_incomplete' | 'already_claimed';
  email?: string;
  employee?: PrefilledEmployee;
  error?: string;
}

interface CompleteProfileResponse {
  success?: boolean;
  mode?: string;
  request_id?: string;
  error?: string;
}

interface InviteFlowProps {
  token: string;
  onBackToLogin: () => void;
}

const PROFILE_FIELDS = [
  'first_name', 'last_name', 'phone',
  'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship',
  'address_line1', 'address_line2', 'address_city', 'address_state', 'address_zip',
  'dob', 'dl_number', 'shirt_size', 'pants_size', 'jacket_size',
] as const;

type FormFields = Record<typeof PROFILE_FIELDS[number] | 'driver_id' | 'email' | 'password' | 'confirm', string>;

const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

function passwordRules(pw: string) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /[0-9]/.test(pw),
    special: /[!@#$%^&*]/.test(pw),
  };
}

function passwordValid(pw: string): boolean {
  const r = passwordRules(pw);
  return r.length && r.upper && r.lower && r.digit && r.special;
}

export default function InviteFlow({ token, onBackToLogin }: InviteFlowProps) {
  const [kind, setKind] = useState<Kind>('loading');
  const [email, setEmail] = useState('');
  const [form, setForm] = useState<FormFields>({
    driver_id: '', first_name: '', last_name: '', email: '', phone: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    address_line1: '', address_line2: '', address_city: '', address_state: '', address_zip: '',
    dob: '', dl_number: '', shirt_size: '', pants_size: '', jacket_size: '',
    password: '', confirm: '',
  });
  const [driverIdLocked, setDriverIdLocked] = useState(false);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [doneMode, setDoneMode] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    apiPost<InviteResolveResponse>('/api/auth/invite-resolve', { token })
      .then((data) => {
        if (cancelled) return;
        if (!data || !data.kind || data.kind === 'invalid') {
          setKind('invalid');
          return;
        }
        const resolvedEmail = data.email || '';
        setEmail(resolvedEmail);
        if (data.kind === 'already_claimed') {
          setKind('already_claimed');
          return;
        }
        if (data.kind === 'existing_incomplete' && data.employee) {
          const emp = data.employee;
          setForm((prev) => ({
            ...prev,
            driver_id: emp.driver_id || '',
            first_name: emp.first_name || '',
            last_name: emp.last_name || '',
            email: resolvedEmail,
            phone: emp.phone || '',
            emergency_contact_name: emp.emergency_contact_name || '',
            emergency_contact_phone: emp.emergency_contact_phone || '',
            emergency_contact_relationship: emp.emergency_contact_relationship || '',
            address_line1: emp.address_line1 || '',
            address_line2: emp.address_line2 || '',
            address_city: emp.address_city || '',
            address_state: emp.address_state || '',
            address_zip: emp.address_zip || '',
            dob: emp.dob || '',
            dl_number: emp.dl_number || '',
            shirt_size: emp.shirt_size || '',
            pants_size: emp.pants_size || '',
            jacket_size: emp.jacket_size || '',
          }));
          setDriverIdLocked(!!emp.driver_id);
          setKind('existing_incomplete');
          return;
        }
        // kind === 'new'
        setForm((prev) => ({ ...prev, email: resolvedEmail }));
        setDriverIdLocked(false);
        setKind('new');
      })
      .catch(() => {
        if (!cancelled) setKind('invalid');
      });
    return () => { cancelled = true; };
  }, [token]);

  function updateField<K extends keyof FormFields>(field: K, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const isExisting = kind === 'existing_incomplete';
  const pwd = passwordRules(form.password);

  // Wizard steps — both new and existing share the structure; existing adds Password.
  const STEPS = isExisting
    ? [
        { title: 'Welcome', required: [] as string[] },
        { title: 'Identity', required: ['first_name', 'last_name'] },
        { title: 'Profile', required: [] as string[] },
        { title: 'Set Password', required: [] as string[] },
        { title: 'Review & Submit', required: [] as string[] },
      ]
    : [
        { title: 'Welcome', required: [] as string[] },
        { title: 'Identity', required: ['first_name', 'last_name', 'driver_id'] },
        { title: 'Profile', required: [] as string[] },
        { title: 'Review & Submit', required: [] as string[] },
      ];

  function canProceed(): boolean {
    const required = STEPS[step]?.required || [];
    if (step === 1 && !isExisting && form.driver_id.length !== 7) return false;
    if (isExisting && STEPS[step].title === 'Set Password') {
      return passwordValid(form.password) && form.password === form.confirm;
    }
    return required.every((f) => !!(form as Record<string, string>)[f]);
  }

  function handleSubmit() {
    setSubmitting(true);
    setError('');
    const payload: Record<string, string | undefined> = {
      invite_token: token,
      driver_id: form.driver_id,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email || undefined,
      phone: form.phone || undefined,
      emergency_contact_name: form.emergency_contact_name || undefined,
      emergency_contact_phone: form.emergency_contact_phone || undefined,
      emergency_contact_relationship: form.emergency_contact_relationship || undefined,
      address_line1: form.address_line1 || undefined,
      address_line2: form.address_line2 || undefined,
      address_city: form.address_city || undefined,
      address_state: form.address_state || undefined,
      address_zip: form.address_zip || undefined,
      dob: form.dob || undefined,
      dl_number: form.dl_number || undefined,
      shirt_size: form.shirt_size || undefined,
      pants_size: form.pants_size || undefined,
      jacket_size: form.jacket_size || undefined,
    };
    if (isExisting) {
      payload.password = form.password;
      payload.confirm = form.confirm;
    }
    apiPost<CompleteProfileResponse>('/api/auth/complete-profile', payload)
      .then((data) => {
        setSubmitting(false);
        if (data.error) { setError(data.error); return; }
        setDoneMode(data.mode || (isExisting ? 'updated' : 'pending'));
        setDone(true);
      })
      .catch(() => {
        setSubmitting(false);
        setError('Connection error. Please try again.');
      });
  }

  // --- helpers ---
  function labeledInput(
    label: string,
    field: keyof FormFields,
    opts: { required?: boolean; type?: string; inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']; maxLength?: number; placeholder?: string; disabled?: boolean } = {},
  ) {
    const missing = !!opts.required && !form[field];
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
          {label}{opts.required ? ' *' : ''}
        </div>
        <input
          type={opts.type || 'text'}
          inputMode={opts.inputMode}
          maxLength={opts.maxLength}
          placeholder={opts.placeholder}
          value={form[field]}
          disabled={opts.disabled}
          onChange={(e) => {
            const v = opts.maxLength && opts.inputMode === 'numeric'
              ? e.target.value.replace(/\D/g, '').slice(0, opts.maxLength)
              : e.target.value;
            updateField(field, v);
          }}
          style={{
            ...INPUT,
            borderColor: missing ? C.red : 'rgba(255,255,255,0.08)',
            opacity: opts.disabled ? 0.6 : 1,
            cursor: opts.disabled ? 'not-allowed' : 'text',
          }}
        />
      </div>
    );
  }

  function labeledSelect(label: string, field: keyof FormFields, options: string[]) {
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
          {label}
        </div>
        <select
          value={form[field]}
          onChange={(e) => updateField(field, e.target.value)}
          style={{ ...INPUT, appearance: 'none' as const }}
        >
          <option value="">Select...</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  function rule(label: string, ok: boolean) {
    return (
      <div style={{ fontFamily: FONT.mono, fontSize: 11, color: ok ? C.green : C.textMuted, marginBottom: 2 }}>
        {ok ? '\u2713 ' : '\u2022 '}{label}
      </div>
    );
  }

  // --- terminal screens ---

  if (kind === 'loading') {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: C.dark }}>
        <div style={{ ...GLASS_STRONG, maxWidth: 420, width: '100%', padding: 28, borderRadius: 20, textAlign: 'center' }}>
          <div style={{ fontFamily: FONT.mono, fontSize: 13, color: C.silver }}>Resolving invite…</div>
        </div>
      </div>
    );
  }

  if (kind === 'invalid') {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: C.dark }}>
        <div style={{ ...GLASS_STRONG, maxWidth: 420, width: '100%', padding: 32, borderRadius: 20, textAlign: 'center' }}>
          <div style={{ fontFamily: FONT.headline, fontSize: 22, color: C.text, marginBottom: 10 }}>Invite link invalid</div>
          <div style={{ fontFamily: FONT.mono, fontSize: 13, color: C.silver, lineHeight: 1.6, marginBottom: 20 }}>
            This invite link is missing, malformed, or expired. Ask your manager for a fresh link.
          </div>
          <button style={BTN} onClick={onBackToLogin}>Back to sign in</button>
        </div>
      </div>
    );
  }

  if (kind === 'already_claimed') {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: C.dark }}>
        <div style={{ ...GLASS_STRONG, maxWidth: 420, width: '100%', padding: 32, borderRadius: 20, textAlign: 'center' }}>
          <div style={{ fontFamily: FONT.headline, fontSize: 22, color: C.text, marginBottom: 10 }}>Account already set up</div>
          <div style={{ fontFamily: FONT.mono, fontSize: 13, color: C.silver, lineHeight: 1.6, marginBottom: 8 }}>
            {email ? email + ' is already registered.' : 'This account is already registered.'}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 12, color: C.textMuted, marginBottom: 20 }}>
            Sign in with your driver ID and password. Use “Forgot password” from the sign-in screen if needed.
          </div>
          <button style={BTN} onClick={onBackToLogin}>Go to sign in</button>
        </div>
      </div>
    );
  }

  if (done) {
    const isUpdated = doneMode === 'updated';
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: C.dark }}>
        <div style={{ ...GLASS_STRONG, padding: 40, maxWidth: 420, width: '100%', borderRadius: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>{'\u2713'}</div>
          <div style={{ fontFamily: FONT.headline, fontSize: 24, color: C.text, marginBottom: 8 }}>
            {isUpdated ? 'Profile Complete!' : 'Registration Submitted'}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 14, color: C.silver, marginBottom: 20, lineHeight: 1.6 }}>
            {isUpdated
              ? 'Your profile is verified and your password is set. You can sign in now.'
              : 'Your registration has been submitted for manager review. You will be able to sign in once approved.'}
          </div>
          <button style={BTN} onClick={onBackToLogin}>Go to sign in</button>
        </div>
      </div>
    );
  }

  // --- wizard ---

  const progress = ((step + 1) / STEPS.length) * 100;
  const isLastStep = step === STEPS.length - 1;
  let content: React.ReactNode = null;

  switch (STEPS[step].title) {
    case 'Welcome':
      content = (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ fontFamily: FONT.headline, fontSize: 24, color: C.text, marginBottom: 8 }}>
            {isExisting ? 'Welcome back' : 'Welcome to Prestige World Drive'}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 13, color: C.silver, lineHeight: 1.6, marginBottom: 16 }}>
            {isExisting
              ? 'Your account exists but is not yet set up. Confirm your info and set a password to finish.'
              : 'This form creates your driver account. Your manager will review and approve your registration.'}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, ...GLASS, padding: 12, borderRadius: 10 }}>
            Invite for <span style={{ color: C.gold }}>{email}</span>
          </div>
        </div>
      );
      break;

    case 'Identity':
      content = (
        <div>
          {labeledInput('First name', 'first_name', { required: true })}
          {labeledInput('Last name', 'last_name', { required: true })}
          {labeledInput('Driver ID (7 digits)', 'driver_id', {
            required: !isExisting,
            inputMode: 'numeric',
            maxLength: 7,
            placeholder: '1234567',
            disabled: driverIdLocked,
          })}
          {labeledInput('Email', 'email', { type: 'email', disabled: true })}
          {labeledInput('Phone (optional)', 'phone', { type: 'tel' })}
        </div>
      );
      break;

    case 'Profile':
      content = (
        <div>
          <div style={{ fontFamily: FONT.mono, fontSize: 10, color: C.textMuted, marginBottom: 12, ...GLASS, padding: 10, borderRadius: 8 }}>
            Optional fields — you can leave blank and update later.
          </div>
          {labeledInput('Emergency contact name', 'emergency_contact_name')}
          {labeledInput('Emergency contact phone', 'emergency_contact_phone', { type: 'tel' })}
          {labeledInput('Relationship', 'emergency_contact_relationship')}
          {labeledInput('Address line 1', 'address_line1')}
          {labeledInput('Address line 2', 'address_line2')}
          {labeledInput('City', 'address_city')}
          {labeledInput('State', 'address_state')}
          {labeledInput('ZIP', 'address_zip')}
          {labeledInput('Date of birth', 'dob', { type: 'date' })}
          {labeledInput('Driver license #', 'dl_number')}
          {labeledSelect('Shirt size', 'shirt_size', SIZES)}
          {labeledInput('Pants size', 'pants_size')}
          {labeledSelect('Jacket size', 'jacket_size', SIZES)}
        </div>
      );
      break;

    case 'Set Password':
      content = (
        <div>
          <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.gold, marginBottom: 10 }}>Set your account password</div>
          {labeledInput('Password', 'password', { type: 'password', required: true })}
          {labeledInput('Confirm password', 'confirm', { type: 'password', required: true })}
          <div style={{ marginTop: 8 }}>
            {rule('At least 8 characters', pwd.length)}
            {rule('Uppercase letter', pwd.upper)}
            {rule('Lowercase letter', pwd.lower)}
            {rule('Number', pwd.digit)}
            {rule('Special character (!@#$%^&*)', pwd.special)}
            {form.confirm
              ? rule('Passwords match', form.password === form.confirm)
              : null}
          </div>
        </div>
      );
      break;

    case 'Review & Submit':
      content = (
        <div>
          <div style={{ fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 12 }}>
            Review your information. Tap Back to edit.
          </div>
          {(
            [
              { label: 'Name', value: (form.first_name + ' ' + form.last_name).trim() || '—' },
              { label: 'Driver ID', value: form.driver_id || '—' },
              { label: 'Email', value: form.email || '—' },
              { label: 'Phone', value: form.phone || '—' },
              { label: 'Emergency', value: form.emergency_contact_name ? form.emergency_contact_name + ' (' + form.emergency_contact_phone + ')' : '—' },
              { label: 'Address', value: form.address_line1 ? [form.address_line1, form.address_city, form.address_state].filter(Boolean).join(', ') : '—' },
              { label: 'DOB', value: form.dob || '—' },
              { label: 'DL #', value: form.dl_number || '—' },
              { label: 'Shirt', value: form.shirt_size || '—' },
              { label: 'Pants', value: form.pants_size || '—' },
              { label: 'Jacket', value: form.jacket_size || '—' },
            ]
          ).map((item) => (
            <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase' }}>{item.label}</span>
              <span style={{ fontFamily: FONT.mono, fontSize: 13, color: item.value === '—' ? C.textMuted : C.text, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{item.value}</span>
            </div>
          ))}
          <div style={{ fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, marginTop: 14 }}>
            {isExisting
              ? 'On submit: your profile updates immediately and your password is set.'
              : 'On submit: your registration goes to your manager for review.'}
          </div>
        </div>
      );
      break;
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: C.dark }}>
      <div style={{ ...GLASS_STRONG, maxWidth: 460, width: '100%', padding: 28, borderRadius: 20 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontFamily: FONT.mono, fontSize: 11, color: C.gold }}>Step {step + 1} of {STEPS.length}</span>
            <span style={{ fontFamily: FONT.mono, fontSize: 11, color: C.silver }}>{STEPS[step].title}</span>
          </div>
          <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
            <div style={{ height: '100%', width: progress + '%', background: C.gold, borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
        </div>

        <div style={{ minHeight: 220, marginBottom: 20 }}>{content}</div>

        {error ? (
          <div style={{ fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: 'center', marginBottom: 12 }}>{error}</div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          {step > 0 ? (
            <button
              type="button"
              style={{ ...GLASS, padding: '12px 20px', color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: 'pointer', minHeight: 48, border: '1px solid rgba(255,255,255,0.08)' }}
              onClick={() => { setStep(step - 1); setError(''); }}
              disabled={submitting}
            >
              {'\u2190 Back'}
            </button>
          ) : (
            <button
              type="button"
              style={{ ...GLASS, padding: '12px 20px', color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: 'pointer', minHeight: 48, border: '1px solid rgba(255,255,255,0.08)' }}
              onClick={onBackToLogin}
              disabled={submitting}
            >
              Cancel
            </button>
          )}

          {!isLastStep ? (
            <button
              type="button"
              style={canProceed() ? BTN : BTN_DISABLED}
              disabled={!canProceed()}
              onClick={() => { setStep(step + 1); setError(''); }}
            >
              Next {'\u2192'}
            </button>
          ) : (
            <button
              type="button"
              style={submitting ? BTN_DISABLED : BTN}
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Submitting…' : (isExisting ? 'Complete setup' : 'Submit registration')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
